import React, { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import UserRow from "@/components/admin/UserRow";
import UserCreateModal from "@/components/admin/UserCreateModal";

const TABLE_HEADERS = [
  "Email",
  "Role",
  "Show prices",
  "Status",
  "Created",
  "Last login",
  "",
];

/**
 * Users tab — list + create/reset/disable flows.
 */
export default function UsersTab() {
  const [users, setUsers] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newPwAlert, setNewPwAlert] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const onPatch = async (id, fields) => {
    const { data } = await api.patch(`/admin/users/${id}`, fields);
    setUsers((rs) => rs.map((u) => (u.id === id ? data : u)));
  };
  const onReset = async (id, email) => {
    if (!window.confirm(`Reset password for ${email}?`)) return;
    const { data } = await api.post(`/admin/users/${id}/reset-password`);
    setNewPwAlert({ email, password: data.new_password });
  };
  const onDelete = async (id, email) => {
    if (!window.confirm(`Disable ${email}? They will lose access.`)) return;
    await api.delete(`/admin/users/${id}`);
    await load();
  };

  if (!users) return <div className="eyebrow text-charcoal/55">Loading…</div>;

  return (
    <section data-testid="admin-users-tab">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-charcoal text-3xl">Users</h2>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="btn-brass text-charcoal"
          data-testid="admin-new-user-btn"
        >
          <Plus size={15} /> New user
        </button>
      </div>
      <span className="block bg-brass mt-4" style={{ width: 60, height: 1 }} />

      {newPwAlert && (
        <div
          className="mt-6 p-4 border border-brass/40 bg-pearl/30"
          data-testid="admin-new-password-alert"
        >
          <p className="eyebrow text-brass">New password — copy now</p>
          <p className="font-display mt-2 text-charcoal">
            <span className="text-charcoal/55">{newPwAlert.email}</span> ·{" "}
            <code className="font-mono text-base">{newPwAlert.password}</code>
          </p>
          <button
            type="button"
            className="mt-3 eyebrow text-charcoal/60 hover:text-brass"
            onClick={() => setNewPwAlert(null)}
            data-testid="admin-new-password-dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      <table className="w-full mt-10 text-sm" data-testid="admin-users-table">
        <thead>
          <tr className="text-left text-charcoal/55">
            {TABLE_HEADERS.map((h) => (
              <th
                key={h || "actions"}
                className="eyebrow font-normal py-3 pr-4"
                style={{ fontSize: 10 }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-charcoal">
          {users.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              onPatch={onPatch}
              onReset={onReset}
              onDelete={onDelete}
            />
          ))}
        </tbody>
      </table>

      {creating && (
        <UserCreateModal
          onClose={() => setCreating(false)}
          onCreated={async (created) => {
            setCreating(false);
            await load();
            setNewPwAlert({
              email: created.email,
              password: created._password,
            });
          }}
        />
      )}
    </section>
  );
}
